import { runInAction } from 'mobx';

/**
 * PSTUDIO-4215: the route → tableStore rules, in one place.
 *
 * They used to live inline in `use-table-store-sync`'s effect, where no test
 * could reach them — and the defect this file exists for was exactly one of
 * them: the branch that clears the table when the route names no draft version
 * dropped the ROWS but kept `solutionVersionId` pointing at the version those
 * rows came from. The store then claimed to be showing a version whose rows it
 * had just thrown away, and because the MobX reaction that fetches nodes only
 * runs when that id CHANGES, re-entering the same solution issued no fetch at
 * all: Workflows rendered `0 Nodes`, with no spinner and no error, until a hard
 * reload (measured on main, 2026-09-18 — the round trip made no
 * `GetProStudioVersionData` request whatsoever).
 *
 * The invariant these rules keep is one line, and its scope is this layer: when
 * THIS function empties the table, it releases the identity in the same step.
 * It is not a property of the store at large — `processVersionData` also empties
 * `rows` (the "version has no nodes" arm) and deliberately keeps the id.
 */

/** The part of `tableStore` these rules touch. */
export interface TableStoreRouteSyncTarget {
  botId: string;
  /**
   * A plain field here, a SETTER on the real store: assigning it flushes both
   * save queues and the deferred-delete track, and runs the module-level
   * reaction synchronously (`stores/table-store.ts`, `set solutionVersionId`).
   * Writing it is not a bookkeeping poke — see `table-store-version-identity`
   * for the tests that drive this through the real store.
   */
  solutionVersionId: string;
  projectProStudioSettingId: string;
  /** `string | undefined` on the store itself — kept verbatim, not narrowed. */
  draftVersion: string | undefined;
  rows: { length: number };
  clearTable(): void;
  /** PSTUDIO-4450: rows fed from the collab document. */
  liveEditingSource: boolean;
  setLiveEditingSource(on: boolean): void;
  /** `clearTable` without its `validateAndSync` upload. */
  dropDataset(): void;
}

export interface TableStoreRouteSyncInput {
  /** The Workflows page owns the store itself (see `builder.tsx`). */
  isWorkflowsPage: boolean;
  /**
   * What the caller calls a first-level page — Configurations, Workflows, and
   * nominally Home. Two cautions, both measured rather than assumed:
   * `isWorkflowsPage` is checked FIRST above, so Workflows never reaches this
   * arm; and Home never sets it, because the route is declared with
   * `label: 'Solutions'` (`hooks/use-pro-studio-routes.tsx`) while the hook
   * compares against `ProStudioPages.HOME` (`'Home'`). In practice this arm
   * fires for Configurations.
   */
  isFirstLevelPage: boolean;
  /** `ctx.draftVersion` — null/undefined while the solution is (re)loading. */
  draftVersion?: string | null;
  botId?: string;
  projectId?: string;
  /** `buildSolutionVersionId(...)` — `''` when any part is missing. */
  solutionVersionId: string;
  /**
   * PSTUDIO-4449: whether this draft's rows come from its collab document
   * (`canFeedFromCollab`). Off the Workflows page nothing else sets it, and it
   * must be in place when the version reaction sees the id — set in the same
   * action, ahead of it — or the reaction fetches from Hasura, which for a v2
   * draft answers null: the store then waits for data for good and Publish
   * stays disabled.
   */
  liveSource?: boolean;
}

/**
 * The writes are wrapped in one action deliberately. The `solutionVersionId`
 * reaction is declared with `delay: 0`, and mobx turns that into
 * `runSync = !opts.scheduler && !opts.delay` — i.e. SYNCHRONOUS, firing the
 * moment the id is assigned. Written field by field, it would therefore observe
 * the new identity while `draftVersion` still held the old value, and
 * `setState()` forces `'viewing'` whenever `draftVersion` is empty — so the
 * loader this function is supposed to raise would collapse into an empty table,
 * which is the very symptom PSTUDIO-4215 is about.
 */
export function applyTableStoreRouteSync(
  store: TableStoreRouteSyncTarget,
  {
    isWorkflowsPage,
    isFirstLevelPage,
    draftVersion,
    botId,
    projectId,
    solutionVersionId,
    liveSource,
  }: TableStoreRouteSyncInput
): void {
  if (isWorkflowsPage) {
    return;
  }

  runInAction(() => {
    applyRules(store, {
      isFirstLevelPage,
      draftVersion,
      botId,
      projectId,
      solutionVersionId,
      liveSource,
    });
  });
}

function applyRules(
  store: TableStoreRouteSyncTarget,
  {
    isFirstLevelPage,
    draftVersion,
    botId,
    projectId,
    solutionVersionId,
    liveSource,
  }: Omit<TableStoreRouteSyncInput, 'isWorkflowsPage'>
): void {
  if (isFirstLevelPage) {
    store.botId = '';
    store.solutionVersionId = '';
    store.projectProStudioSettingId = '';
    // PSTUDIO-4449: no feed outside the solution, so no collab source either.
    if (store.liveEditingSource) store.setLiveEditingSource(false);

    return;
  }

  if (!draftVersion) {
    // PSTUDIO-4215: the rows go, and the identity goes WITH them.
    //
    // What puts `draftVersion` at null on the way BACK into a solution is
    // `selectBot`, which re-applies `initialBotRelatedContext`
    // (`xstate/prostudio.xstate.ts` — the object holds `draftVersion: null`, and
    // both solution-entry transitions spread it), after which `loadBotVersions`
    // settles it on the SAME value it had. Not `clearSolutionScope`: that one
    // clears `botName` and leaves `draftVersion` alone. Measured on main
    // (2026-09-18) by logging this hook through a leave-and-return round trip:
    // three passes arrive with `draftVersion: null` before it comes back as
    // `v1`.
    //
    // Clearing the rows alone left the store holding the id of a version it no
    // longer had rows for, and re-assigning an UNCHANGED id is a no-op — so the
    // `solutionVersionId` reaction, which is what issues the fetch, never ran a
    // second time and the table stayed empty (measured: zero
    // `GetProStudioVersionData` requests on that second entry).
    //
    // Giving the id up makes that assignment a real change again. It also lets
    // the reaction close the version's subscription (it tears the previous visit
    // down on any identity change) instead of leaving one open for a version the
    // store has stopped showing.
    if (store.rows.length > 0) {
      // PSTUDIO-4450: rows fed from the collab document are not Hasura's —
      // drop them without `clearTable`'s validate-and-upload.
      if (store.liveEditingSource) {
        store.dropDataset();
      } else {
        store.clearTable();
      }
    }
    if (store.solutionVersionId !== '') {
      store.solutionVersionId = '';
    }
    return;
  }

  if (liveSource !== undefined && store.liveEditingSource !== liveSource) {
    store.setLiveEditingSource(liveSource);
  }

  if (botId) {
    store.botId = botId;
  }

  if (projectId) {
    store.projectProStudioSettingId = projectId;
  }

  // Before the identity, not after: the reaction reads `draftVersion` through
  // `setState()`, and the action above is belt — this is braces.
  if (draftVersion !== store.draftVersion) {
    store.draftVersion = draftVersion;
  }

  if (solutionVersionId && solutionVersionId !== store.solutionVersionId) {
    store.solutionVersionId = solutionVersionId;
  }
}
